import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX, FiCheck, FiPhone, FiMail, FiUser, FiFileText } from "react-icons/fi";
import { NotebookPen } from "lucide-react"
import { useAuth } from "../../contexts/AuthContext";
import { createCustomer, findMatchingCustomer } from "../../lib/supabase/customers";
import { useAustralianPhone } from "../../hooks/useAustralianPhone";

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, error, children }) => (
    <div>
        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest block mb-1.5">
            {label}
        </label>
        {children}
        {error && <p className="text-[10px] text-red-500 mt-1 leading-tight">{error}</p>}
    </div>
);

const CUSTOMER_TYPES = ["None", "Account", "COD"];

// ─── Main component ───────────────────────────────────────────────────────────
const NewCustomerModal = ({ onClose, onCreated }) => {
    const { company, userProfile } = useAuth();
    const colorPickerRef = useRef(null);

    const [form, setForm] = useState({
        name: "",
        email: "",
        contact_name: "",
        note: "",
        customer_type: "None",
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const phone = useAustralianPhone("");

    // Close on Escape
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [onClose]);

    const initials = (form.name || "?")
        .split(" ").slice(0, 2).map(n => n[0] || "").join("").toUpperCase() || "?";

    const set = (field) => (e) => {
        setForm(f => ({ ...f, [field]: e.target.value }));
        setErrors(err => ({ ...err, [field]: undefined }));
    };


    const validate = () => {
        const errs = {};

        if (!form.name.trim()) {
            errs.name = "Customer name is required";
        }

        const email = form.email.trim();

        if (!email) {
            errs.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errs.email = "Invalid email address";
        }

        if (!form.contact_name.trim()) {
            errs.contact_name = "Contact name is required";
        }

        if (!phone.value.trim()) {
            errs.phone = "Phone is required";
        } else if (!phone.isValid) {
            errs.phone = "Enter a valid Australian number (e.g. 0412 345 678)";
        }

        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(true);
        try {
            const payload = {
                company_id: company.id,
                created_by: userProfile?.id ?? null,
                name: form.name.trim(),
                email: form.email.trim() || null,
                contact_name: form.contact_name.trim() || null,
                note: form.note.trim() || null,
                phone: phone.getE164(),
                customer_type: form.customer_type === "None" ? null : form.customer_type,
            };
            const existing = await findMatchingCustomer(company.id, payload);
            if (existing) {
                setErrors({ name: "A customer with this name, email and phone already exists" });
                return;
            }

            const customer = await createCustomer(payload);
            onCreated?.(customer);
            onClose();
        } catch (err) {
            if (err?.code === "23505") {
                setErrors({ name: "A customer with these details already exists" });
            } else {
                console.error("Failed to create customer:", err);
                setErrors({ name: "Something went wrong. Please try again." });
            }
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="mobile-dialog-shell fixed inset-0 z-[200] flex items-center justify-center px-4 !mt-0">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />

            {/* Card */}
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-[15px] font-bold text-zinc-800">New Customer</h2>
                        <p className="text-[11px] text-gray-400 mt-0.5">Fill in the details below</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-zinc-700 hover:bg-gray-100 transition-colors"
                    >
                        <FiX size={15} />
                    </button>
                </div>

                {/* Avatar */}
                <div className="flex flex-col items-center py-6 bg-gray-50 border-b border-gray-100">
                    <div className="relative" ref={colorPickerRef}>
                        <div
                            className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold cursor-pointer select-none transition-transform hover:scale-105 bg-zinc-700"
                        >
                            {initials}
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="px-6 py-5 flex flex-col gap-4">

                    {/* Name */}
                    <Field label="Customer Name *" error={errors.name}>
                        <input
                            autoFocus
                            type="text"
                            value={form.name}
                            onChange={set("name")}
                            onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
                            placeholder="e.g. Smith Constructions"
                            className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-300 transition-colors
                                ${errors.name ? "border-red-300 bg-red-50" : "border-gray-200"}`}
                        />
                    </Field>

                    {/* Email */}
                    <Field label="Email *" error={errors.email}>
                        <div className="relative">
                            <FiMail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                            <input
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                placeholder="email@example.com"
                                className={`w-full pl-8 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-300 transition-colors
                                    ${errors.email ? "border-red-300 bg-red-50" : "border-gray-200"}`}
                            />
                        </div>
                    </Field>

                    {/* Contact name */}
                    <Field label="Contact Name *" error={errors.contact_name}>
                        <div className="relative">
                            <FiUser size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                            <input
                                type="text"
                                value={form.contact_name}
                                onChange={set("contact_name")}
                                placeholder="Contact person's name"
                                className={`w-full pl-8 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-300 transition-colors
                                    ${errors.contact_name ? "border-red-300 bg-red-50" : "border-gray-200"}`}
                            />
                        </div>
                    </Field>

                    {/* Phone */}
                    <Field label="Phone *" error={errors.phone}>
                        <div className="relative">
                            <FiPhone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                            <input
                                type="tel"
                                inputMode="numeric"
                                value={phone.value}
                                onChange={(e) => {
                                    phone.handleChange(e);
                                    setErrors(err => ({ ...err, phone: undefined }));
                                }}
                                placeholder="0412 345 678"
                                className={`w-full pl-8 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-300 transition-colors
                                    ${errors.phone ? "border-red-300 bg-red-50" : "border-gray-200"}`}
                            />
                        </div>
                    </Field>

                    <Field label="Customer Type">
                        <div className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1 border border-gray-200">
                            {CUSTOMER_TYPES.map(type => {
                                const active = form.customer_type === type;

                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() =>
                                            setForm(f => ({
                                                ...f,
                                                customer_type: type,
                                            }))
                                        }
                                        className={`relative rounded-lg px-3 py-2 text-xs font-bold transition-all ${active
                                            ? "bg-zinc-800 text-white shadow-sm"
                                            : "text-gray-500 hover:text-zinc-800 hover:bg-white/70"
                                            }`}
                                    >
                                        {type}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    {/* Note */}
                    <Field label="Notes" error={errors.note}>
                        <div className="relative">
                            <NotebookPen size={13} className="absolute left-3 top-[1.3rem] -translate-y-1/2 text-gray-300 pointer-events-none" />
                            <textarea
                                type="text"
                                value={form.note}
                                onChange={set("note")}
                                placeholder="e.g. Always prefers before lunch delivery"
                                className={`w-full resize-none pl-8 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-300 transition-colors
                                    ${errors.note ? "border-red-300 bg-red-50" : "border-gray-200"}`}
                            />
                        </div>
                    </Field>

                    {/* Actions */}
                    <div className="flex items-center gap-2.5 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={saving}
                            className="flex-1 py-2.5 text-sm font-semibold text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {saving
                                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <FiCheck size={14} />
                            }
                            {saving ? "Saving…" : "Create Customer"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NewCustomerModal;
